
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Home as HomeIcon, Info, Calendar, CalendarRange, Users, FileText, Mail, BriefcaseBusiness, Trophy, ChevronDown, User, BookOpen, ClipboardList, ExternalLink } from 'lucide-react';
import historyData from './data/stateChampionshipHistory.json';
import individualHistoryData from './data/stateChampionshipIndividuals.json';

const Navbar = ({ currentPage, setCurrentPage, navItems }) => {
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [openMobileDropdown, setOpenMobileDropdown] = useState(null);
    const [openDesktopDropdown, setOpenDesktopDropdown] = useState(null);

    const handleNavClick = (page) => {
        setCurrentPage(page);
        setIsMobileMenuOpen(false);
        setOpenMobileDropdown(null);
        setOpenDesktopDropdown(null);
    };

    const isNavItemActive = (item) => {
        if (item.children) {
            return item.children.some((child) => child.name === currentPage);
        }
        return currentPage === item.name;
    };

    const navButtonClass = (isActive) =>
        `flex items-center px-3 py-2 rounded-md text-sm font-medium transition-colors duration-150
        ${isActive
            ? 'text-white bg-blue-600'
            : 'text-gray-300 hover:text-white hover:bg-gray-700'
        }`;

    return (
        <nav className="bg-gray-800 shadow-lg sticky top-0 z-50">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex justify-between items-center h-16">
                    {/* Logo and Site Title */}
                    <div className="flex-shrink-0 flex items-center">
                        <img src="images/MIQBALogo.png" alt="MIQBA Logo" className="h-12 w-12" />
                        <span className="ml-3 text-2xl font-bold text-white">MIQBA</span>
                    </div>

                    {/* Desktop Navigation */}
                    <div className="hidden md:flex md:items-center md:space-x-4">
                        {navItems.map((item) => {
                            const { name, display, icon: Icon, children } = item;

                            if (children) {
                                return (
                                    <div
                                        key={name}
                                        className="relative"
                                        onMouseEnter={() => setOpenDesktopDropdown(name)}
                                        onMouseLeave={() => setOpenDesktopDropdown(null)}
                                    >
                                        <button
                                            className={navButtonClass(isNavItemActive(item))}
                                            aria-haspopup="true"
                                            aria-expanded={openDesktopDropdown === name}
                                        >
                                            <Icon size={18} className="mr-2" />
                                            {display}
                                            <ChevronDown size={16} className="ml-1" />
                                        </button>
                                        {openDesktopDropdown === name && (
                                            <div className="absolute left-0 top-full z-50">
                                                <div className="h-2" aria-hidden="true" />
                                                <div className="bg-gray-800 rounded-md shadow-lg border border-gray-700 py-1 min-w-[240px] -mt-2">
                                                    {children.map(({ name: childName, display: childDisplay, icon: ChildIcon }) => (
                                                        <button
                                                            key={childName}
                                                            onClick={() => handleNavClick(childName)}
                                                            className={`flex items-center w-full px-4 py-2 text-sm font-medium transition-colors duration-150
                                                                ${currentPage === childName
                                                                    ? 'text-white bg-blue-600'
                                                                    : 'text-gray-300 hover:text-white hover:bg-gray-700'
                                                                }`}
                                                        >
                                                            <ChildIcon size={18} className="mr-2" />
                                                            {childDisplay}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                );
                            }

                            return (
                                <button
                                    key={name}
                                    onClick={() => handleNavClick(name)}
                                    className={navButtonClass(isNavItemActive(item))}
                                >
                                    <Icon size={18} className="mr-2" />
                                    {display}
                                </button>
                            );
                        })}
                    </div>

                    {/* Mobile Menu Button */}
                    <div className="md:hidden flex items-center">
                        <button
                            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                            className="inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-white hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500"
                            aria-expanded={isMobileMenuOpen}
                        >
                            <span className="sr-only">Open main menu</span>
                            {/* Icon for menu (hamburger) */}
                            {!isMobileMenuOpen ? (
                                <svg className="block h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16m-7 6h7" />
                                </svg>
                            ) : (
                                // Icon for close (X)
                                <svg className="block h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            )}
                        </button>
                    </div>
                </div>
            </div>

            {/* Mobile Menu Dropdown */}
            {isMobileMenuOpen && (
                <div className="md:hidden shadow-lg border-t border-gray-700 bg-gray-800">
                    <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
                        {navItems.map((item) => {
                            const { name, display, icon: Icon, children } = item;

                            if (children) {
                                return (
                                    <div key={name}>
                                        <button
                                            onClick={() => setOpenMobileDropdown(openMobileDropdown === name ? null : name)}
                                            className={`flex items-center w-full px-3 py-2 rounded-md text-base font-medium transition-colors duration-150
                                                ${isNavItemActive(item)
                                                    ? 'text-white bg-blue-600'
                                                    : 'text-gray-300 hover:text-white hover:bg-gray-700'
                                                }`}
                                        >
                                            <Icon size={20} className="mr-3" />
                                            {display}
                                            <ChevronDown size={18} className={`ml-auto transition-transform duration-150 ${openMobileDropdown === name ? 'rotate-180' : ''}`} />
                                        </button>
                                        {openMobileDropdown === name && (
                                            <div className="pl-4 space-y-1 mt-1">
                                                {children.map(({ name: childName, display: childDisplay, icon: ChildIcon }) => (
                                                    <button
                                                        key={childName}
                                                        onClick={() => handleNavClick(childName)}
                                                        className={`flex items-center w-full px-3 py-2 rounded-md text-base font-medium transition-colors duration-150
                                                            ${currentPage === childName
                                                                ? 'text-white bg-blue-600'
                                                                : 'text-gray-300 hover:text-white hover:bg-gray-700'
                                                            }`}
                                                    >
                                                        <ChildIcon size={20} className="mr-3" />
                                                        {childDisplay}
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                );
                            }

                            return (
                                <button
                                    key={name}
                                    onClick={() => handleNavClick(name)}
                                    className={`flex items-center w-full px-3 py-2 rounded-md text-base font-medium transition-colors duration-150
                                        ${isNavItemActive(item)
                                            ? 'text-white bg-blue-600'
                                            : 'text-gray-300 hover:text-white hover:bg-gray-700'
                                        }`}
                                >
                                    <Icon size={20} className="mr-3" />
                                    {display}
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}
        </nav>
    );
};

const Footer = () => {
    return (
        <footer className="bg-gray-800 text-gray-400 mt-16 border-t border-gray-700">
            <div className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8 text-center">
                <p>&copy; {new Date().getFullYear()} Michigan Quiz Bowl Association</p>
            </div>
        </footer>
    );
};

const InfoCard = ({ title, children, icon: Icon }) => (
    <div className="bg-gray-800 rounded-lg shadow-xl overflow-hidden border border-gray-700">
        <div className="p-6">
            <div className="flex items-center mb-4">
                {Icon && <Icon className="h-8 w-8 text-blue-400 mr-3" />}
                <h3 className="text-2xl font-semibold text-white">{title}</h3>
            </div>
            <div className="text-gray-300 space-y-4">
                {children}
            </div>
        </div>
    </div>
);

const ResourceSection = ({ title, description, icon: Icon, children }) => (
    <div className="bg-gray-800/60 rounded-lg border border-gray-700 p-5 h-full">
        <div className="flex items-center mb-3">
            {Icon && <Icon className="h-5 w-5 text-blue-400 mr-2 flex-shrink-0" />}
            <h4 className="text-lg font-semibold text-white">{title}</h4>
        </div>
        {description && <p className="text-gray-400 text-sm mb-4">{description}</p>}
        <div className="space-y-2">{children}</div>
    </div>
);

const ResourceLink = ({ href, children }) => (
    <a
        href={href}
        target="_blank"
        rel="noreferrer"
        className="flex items-center justify-between gap-3 p-3 rounded-md border border-gray-700/60
            bg-gray-900/40 text-blue-400 hover:text-blue-300 hover:bg-gray-700/40
            hover:border-gray-600 transition-colors duration-150 group"
    >
        <span className="text-sm text-gray-200 group-hover:text-white">{children}</span>
        <ExternalLink size={16} className="flex-shrink-0 text-gray-500 group-hover:text-blue-400" />
    </a>
);

const ResourceCallout = ({ children }) => (
    <div className="rounded-lg border border-blue-600/40 bg-blue-900/20 p-5 text-center text-gray-300">
        {children}
    </div>
);

const Home = ({ setCurrentPage }) => (
    <div className="space-y-12">
        <div className="bg-gradient-to-r from-blue-700 to-indigo-800 text-white rounded-lg shadow-2xl p-8 md:p-12 text-center border border-blue-600">
            <h1 className="text-4xl md:text-5xl font-extrabold mb-4">
                Michigan Quiz Bowl Association
            </h1>
            <p className="text-xl md:text-2xl text-blue-100 mb-8">
                Empowering students and coaches in the state of Michigan to achieve academic excellence
            </p>
            <button
                onClick={() => setCurrentPage('Schedule')}
                className="bg-white text-blue-700 font-bold py-3 px-6 rounded-full text-lg shadow-lg hover:bg-gray-100 transition duration-300 transform hover:scale-105"
            >
                View Upcoming Tournaments
            </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <InfoCard title="MIQBA Format" icon={BriefcaseBusiness}>
                <p>
                    Read the details of what a MIQBA Format competition is, and the benefits of participating in one!
                </p>
                <button
                    onClick={() => setCurrentPage('MIQBAFormat')}
                    className="mt-4 text-blue-400 font-semibold hover:text-blue-300 transition duration-150"
                >
                    Learn More &rarr;
                </button>
            </InfoCard>

            <InfoCard title="For Teams" icon={Users}>
                <p>
                    Find resources for starting a new team, preparing for tournaments, and connecting with other schools in the Michigan circuit.
                </p>
                <button
                    onClick={() => setCurrentPage('CoachResources')}
                    className="mt-4 text-blue-400 font-semibold hover:text-blue-300 transition duration-150"
                >
                    Coach Resources &rarr;
                </button>
            </InfoCard>

            <InfoCard title="Get Involved" icon={Mail}>
                <p>
                    Whether you're a player, coach, or volunteer, there are many ways to get involved. Contact us to find out how you can help support academic excellence.
                </p>
                <button
                    onClick={() => setCurrentPage('Contact')}
                    className="mt-4 text-blue-400 font-semibold hover:text-blue-300 transition duration-150"
                >
                    Contact Us &rarr;
                </button>
            </InfoCard>
        </div>
    </div>
);

const Bylaws = () => (
    <InfoCard title="Our Bylaws" icon={FileText}>
        <h4 className="text-xl font-semibold text-gray-100 mb-2">Our Bylaws</h4>
        <h6 className="text-sm font-semibold text-gray-100 mb-2">
            A PDF of our Bylaws can be viewed <a href="https://drive.google.com/file/d/1yiOcsWWssesqUjT2SnpcIQaSQ74bRRE8/view?usp=sharing">here</a>. Thanks to the Missouri Quiz Bowl Alliance for graciously allowing us to base our bylaws off theirs.
        </h6>
        <div>
            <h5 className="text-lg font-semibold text-gray-100 mb-1">Article I -- Name</h5>
            <div className="mb-3">
                The name of the organization shall be "Michigan Quiz Bowl Association", officially abbreviated "MIQBA".
            </div>
            <h5 className="text-lg font-semibold text-gray-100 mb-1">Article II -- Purpose</h5>
            <div>
                The Michigan Quiz Bowl Association exists to help improve opportunities for students in the state of
                Michigan to participate in academic competition by empowering and educating coaches, and helps to
                oversee an annual High School Quiz Bowl State Championship. The organization shall incorporate under
                Michigan statutes for not-for-profit corporations. Specific objectives of the organization include:
            </div>
            <ol type="a" className="list-[lower-alpha] list-inside mt-2 space-y-1 ms-4 mb-3">
                <li>Helping to organize and direct quiz bowl tournaments. </li>
                <li>Establishing quality standards for quiz bowl tournaments and promoting tournaments that align with the established standards.</li>
                <li>Providing relevant resources and advice to coaches and participants.</li>
                <li>Recognizing students who demonstrate excellence in competition or other contributions to quiz bowl. </li>
                <li>Increasing the number of schools in the state that participate in high quality academic competition. </li>
            </ol>
            <h5 className="text-lg font-semibold text-gray-100 mb-1">Article III -- Membership</h5>
            <h6 className="text-md font-semibold text-gray-100 mb-1">Section 1. Individual Membership Criteria</h6>
            <div>
                Individuals are eligible for membership in the Michigan Quiz Bowl Association if they meet all of the following criteria:
            </div>
            <ol type="a" className="list-[lower-alpha] list-inside mt-2 space-y-1 ms-4 mb-3">
                <li>Members shall be at least eighteen years of age.</li>
                <li>Members shall have completed their secondary education such that they are no longer eligible to compete in high school quiz bowl tournaments. </li>
                <li>At the time membership is granted, a member shall reside within the state of Michigan or within reasonable proximity of the state border such that they may be actively involved in the corporation's activities conducted within the state. Once granted membership, a member shall remain eligible, regardless of residence, if they maintain sufficient involvement in the activities of the organization as determined by the membership. </li>
                <li>Members shall demonstrate consistent support for high-quality quiz bowl and the desire and ability to constructively contribute to the corporation's activities. </li>
            </ol>
            <h6 className="text-md font-semibold text-gray-100 mb-1">Section 2. Membership Classes</h6>
            <ol type="a" className="list-[lower-alpha] list-inside mt-2 space-y-1 ms-4 mb-3">
                <li>The corporation shall have two classes of members: active and inactive.</li>
                <li>Each active member shall be entitled to one vote in all matters decided by a membership vote. Inactive members are ineligible to vote and do not count toward quorum requirements.</li>
                <li>An active member may choose to become inactive by delivering written notice to the Secretary with a justification for inactivity and the length of the expected inactivity not to exceed twelve months.</li>
                <li>The Board of Directors may also, by a majority vote, declare an active member to be inactive after an active member misses three consecutive meetings or three consecutive votes of the full membership.</li>
                <li>An inactive member may become active at any time by delivering written notice to the Secretary with a justification for returning to active member status.</li>
                <li>Upon expiration of an inactivity period, an inactive member shall have thirty days to submit notice to become active or to request an additional inactivity period not to exceed twelve months. If no action is taken within this thirty-day period, the member shall be considered to have voluntarily resigned his or her membership in the organization.</li>
                <li>Requests to renew a member's inactive status shall be approved by a majority vote of the Board of Directors.</li>
            </ol>
            <h6 className="text-md font-semibold text-gray-100 mb-1">Section 3. Admission of Members</h6>
            <ol type="a" className="list-[lower-alpha] list-inside mt-2 space-y-1 ms-4 mb-3">
                <li>At least once per operating year, the Secretary shall call for members to nominate candidates for new membership. The Secretary shall specify a period of no less than three days and no more than seven days for nominations and discussion.</li>
                <li>Any active member may move to open an additional nomination period at any time, which must be seconded and thirded by other active members.</li>
                <li>Eligible individuals may be nominated for membership by any current active member. Nominations must be seconded by another active member.  </li>
                <li>Following the nomination and discussion period, the Secretary shall deliver to all active members via email a new member ballot consisting of the names of all seconded candidates eligible for a vote. Members shall have until 11:59 PM Eastern Time on the seventh day following the send date to cast a "Yes", "No", or "Abstain" vote for each individual.</li>
                <li>If all active members have cast a vote, the President or the Secretary may complete the voting period early.</li>
                <li>Following the voting period, nominees receiving "Yes", "No", or "Abstain" votes from more than half of the active membership and more "Yes" votes than "No" votes shall be offered membership. Nominees failing to meet these thresholds will not become members, but will remain eligible for nomination in the future.</li>
                <li>The President, or another member designated by the President, shall extend invitations for membership to each nominee approved by the current membership.</li>
            </ol>
            <h6 className="text-md font-semibold text-gray-100 mb-1">Section 4. Membership Dues</h6>
            <div className="mb-1">To become an active member, an individual is required to pay a $10 "activation fee" for their
                membership to the organization</div>
            <h6 className="text-md font-semibold text-gray-100 mb-1">Section 5. Membership Records</h6>
            <ol type="a" className="list-[lower-alpha] list-inside mt-2 space-y-1 ms-4 mb-3">
                <li>The corporation shall maintain a list containing the name, physical address, any relevant school affiliation(s), and email address of each current member. The list shall contain a record of the dates each person was granted membership and any dates the member was considered inactive.</li>
                <li>The corporation shall maintain a list of former members containing the dates membership was granted, terminated, and any dates the member's active status changed.</li>
            </ol>
            <h6 className="text-md font-semibold text-gray-100 mb-1">Section 6. Termination of Membership</h6>
            <div>A person's membership shall terminate upon the occurrence of any of the following events: </div>
            <ol type="a" className="list-[lower-alpha] list-inside mt-2 space-y-1 ms-4 mb-3">
                <li>The member delivers a written notice of resignation (physically or digitally) to the President or Secretary. </li>
                <li>The member was considered active during an operating year in which they either did not participate in any activities conducted by the corporation or did not serve on the staff of any quiz bowl tournament meeting the organization's quality standards, unless they resume sufficient participation or begins an inactive status period within the following thirty day period. </li>
                <li>The member is removed by a vote of the membership as prescribed in Section 8 of this article. </li>
                <li>The Board of Directors unanimously determines that the member has engaged in conduct severely detrimental to the purpose or reputation of the corporation.</li>
            </ol>
            <h6 className="text-md font-semibold text-gray-100 mb-1">Section 7. Demonstration of Cause for Member Removal</h6>
            <div>If a motion to terminate a person's membership is initiated by the membership or the Board of Directors:</div>
            <ol type="a" className="list-[lower-alpha] list-inside mt-2 space-y-1 ms-4 mb-3">
                <li>The member shall be notified no less than fifteen days before the proposed effective date of termination.</li>
                <li>Members proposing the termination must show cause justifying the motion for removal.</li>
                <li>The corporation shall provide the member an opportunity to provide testimony to challenge the proposed removal, orally or in writing, no less than five days before the effective date of termination.</li>
            </ol>
            <h6 className="text-md font-semibold text-gray-100 mb-1">Section 8. Removal of Members by Vote</h6>
            <ol type="a" className="list-[lower-alpha] list-inside mt-2 space-y-1 ms-4 mb-3">
                <li>Any active member may move for the removal of another member by notifying the President or Vice President.</li>
                <li>The proposed termination shall be presided over by the Vice President, unless the Vice President is the subject of the proposed member removal, in which case the President shall preside.</li>
                <li>The presiding officer shall notify the active membership of a motion for removal and provide a period of no less than seven days for discussion. The member proposed for removal shall be provided notice and an opportunity to be heard during this period as prescribed in Section 7.</li>
                <li>Following the discussion period, the presiding officer shall deliver to all active members via email a member removal ballot consisting of the names of all individuals proposed for removal. Members shall have until 11:59 PM Eastern Time on the seventh day following the send date to cast a "Yes", "No", or "Abstain" vote for each individual's removal.</li>
                <li>If all active members have cast a vote, the President or the Secretary may complete the voting period early.</li>
                <li>Following the voting period, if a member receives "Yes", "No", or "Abstain" votes from more than half of the active membership and at least twice as many "Yes" votes than "No" votes, and the member has been given the necessary notice and opportunity to be heard, that person's membership shall be terminated.</li>
            </ol>
            <h5 className="text-lg font-semibold text-gray-100 mb-1">Article IV -- Board of Directors</h5>
            <h6 className="text-md font-semibold text-gray-100 mb-1">Section 1. Composition</h6>
            <ol type="a" className="list-[lower-alpha] list-inside mt-2 space-y-1 ms-4 mb-3">
                <li>The officers of the Michigan Quiz Bowl Association shall be the President, Vice President, Secretary, Treasurer, and Director of Outreach, which shall constitute the Board of Directors.</li>
                <li>Any active member may be elected to the Board of Directors.</li>
                <li>No person may concurrently hold more than one office, though in the event of a position being unfilled, the duties of an unfilled position may be fulfilled by another member of the Board of Directors on an as-needed basis.</li>
            </ol>
            <h6 className="text-md font-semibold text-gray-100 mb-1">Section 2. Term of Office</h6>
            <ol type="a" className="list-[lower-alpha] list-inside mt-2 space-y-1 ms-4 mb-3">
                <li>Officers shall be elected to serve a term of four years, unless a shorter term is specified at the time a vote is conducted. Terms shall end on the final day of the operating year.</li>
                <li>An officer may be re-elected to the same position an unlimited number of times.</li>
                <li>A term may end before the designated end date upon the occurrence of any of the following events:
                    <ol type="i" className="list-[lower-roman] list-inside mt-2 space-y-1 ms-4 mb-3">
                        <li>The officer's membership in the corporation is terminated per the provisions in Article III.</li>
                        <li>The officer becomes an inactive member per the provisions in Article III.</li>
                        <li>The officer delivers a written notice of resignation to the President or Secretary.</li>
                        <li>The officer is removed by a vote of the membership as prescribed in Section 4 of this article.</li>
                    </ol>
                </li>
            </ol>
            <h6 className="text-md font-semibold text-gray-100 mb-1">Section 3. Election</h6>
            <ol type="a" className="list-[lower-alpha] list-inside mt-2 space-y-1 ms-4 mb-3">
                <li>The secretary shall call for members to nominate candidates for officer positions in either of the following circumstances:
                    <ol type="i" className="list-[lower-roman] list-inside mt-2 space-y-1 ms-4 mb-3">
                        <li>No later than thirty days before either an officer's term expires or the effective date of an announced vacancy.</li>
                        <li>No later than seven days after an office is vacated or the announcement of an effective date of vacancy made less than thirty days in advance.</li>
                    </ol>
                </li>
                <li>The secretary shall specify a period of no less than three days and no more than seven days for nominations and discussion.</li>
                <li>Any active member may be nominated for any office by another active member and seconded by a third active member.</li>
                <li>Following the nomination and discussion period, the Secretary shall deliver to all active members via email an officer election ballot consisting of the officer positions being filled and the candidates for those positions. Members shall have until 11:59 PM Eastern Time on the seventh day following the send date to rank the candidates in order of preference (1st choice, 2nd choice, etc.)</li>
                <li>If all active members have cast a vote, the President or the Secretary may complete the voting period early.</li>
                <li>Following the voting period, if a candidate receives a simple majority of 1st choice votes, they are elected. If there is no majority, the candidate with the fewest votes is eliminated and ballots for the eliminated candidate transfer to the next-ranked choice. This process is repeated until a candidate receives a majority of the votes. In the event that once the process has narrowed the field down to two candidates the vote is tied, the two candidates in question will compete in a quiz bowl skills challenge, the format of which is to be determined by the combined outgoing and incoming Boards of Directors, excluding the candidates for whom the tiebreaker is being performed.</li>
            </ol>
            <h6 className="text-md font-semibold text-gray-100 mb-1">Section 4. Removal by Member Vote</h6>
            <ol type="a" className="list-[lower-alpha] list-inside mt-2 space-y-1 ms-4 mb-3">
                <li>Any active member may move to remove an officer from the Board of Directors by notifying the President or Vice President. The motion must be seconded by another active member.</li>
                <li>The proposed removal shall be presided over by the President, unless the President is the subject of the proposed removal, in which case the Vice President shall preside.</li>
                <li>Once a proposed removal is seconded, the presiding officer shall deliver to all active members via email a notice that an officer removal vote is scheduled to begin no less than ten days following the send date of the email. Members, including the officer proposed for removal, shall discuss the proposed removal during this period.</li>
                <li>Following the discussion period, the presiding officer shall deliver to all active members via email an officer removal ballot consisting of the names of the officers proposed for removal. Members shall have until 11:59 PM Eastern Time on the seventh day following the send date to cast as "Yes", "No", or "Abstain" vote for each individual's removal.</li>
                <li>If all active members have cast a vote, the President or the Secretary may complete the voting period early.</li>
                <li>Following the voting period, if an officer receives at least twice as many "Yes" votes than "No" votes, that officer shall immediately be removed from office.</li>
            </ol>
            <h6 className="text-md font-semibold text-gray-100 mb-1">Section 5. Duties of President</h6>
            <div>The President shall:</div>
            <ol type="a" className="list-[lower-alpha] list-inside mt-2 space-y-1 ms-4 mb-3">
                <li>supervise the conduct of business of the corporation.</li>
                <li>preside at the meetings of the Michigan Quiz Bowl Association.</li>
                <li>serve as the organization's official liaison with other state organizations that interact with school clubs and extracurricular activities.</li>
                <li>preside over voting proceedings as prescribed by these bylaws or when another member of the Board of Directors would be prescribed to and is unavailable.</li>
            </ol>
            <h6 className="text-md font-semibold text-gray-100 mb-1">Section 6. Duties of Vice-President</h6>
            <div>The Vice-President shall:</div>
            <ol type="a" className="list-[lower-alpha] list-inside mt-2 space-y-1 ms-4 mb-3">
                <li>in the absence of the President, supervise the conduct of the business of the corporation.</li>
                <li>in the absence of the President, preside at the meetings of the Michigan Quiz Bowl Association.</li>
                <li>preside over voting proceedings as prescribed by these bylaws or when the President would be prescribed to and is unavailable.</li>
            </ol>
            <h6 className="text-md font-semibold text-gray-100 mb-1">Section 7. Duties of Secretary</h6>
            <div>The Secretary shall:</div>
            <ol type="a" className="list-[lower-alpha] list-inside mt-2 space-y-1 ms-4 mb-3">
                <li>maintain copies of corporate documents as required by law, including:
                    <ol className="list-[lower-roman] list-inside mt-2 space-y-1 ms-4 mb-3">
                        <li>articles of incorporation and all amendments to them currently in effect.</li>
                        <li>bylaws and all amendments to them currently in effect.</li>
                        <li>the minutes of all meetings of members and records of all actions approved by the members for the past three years.</li>
                        <li>a list of the names and current addresses of the current Board of Directors.</li>
                        <li>the list of the names and current addresses of current members, and the list of former members.</li>
                        <li>the most recent corporate registration report delivered to the secretary of state.</li>
                    </ol>
                </li>
                <li>see that all notices are duly given in accordance with these bylaws or as required by law.</li>
                <li>assist the Director of Outreach in maintaining a list of quiz bowl programs in the state of Michigan along with contact information for their coaches or any other relevant points of contact</li>
            </ol>
            <h6 className="text-md font-semibold text-gray-100 mb-1">Section 8. Duties of Treasurer</h6>
            <div>The Treasurer shall:</div>
            <ol type="a" className="list-[lower-alpha] list-inside mt-2 space-y-1 ms-4 mb-3">
                <li>maintain the corporation's financial accounts, including:
                    <ol type="i" className="list-[lower-roman] list-inside mt-2 space-y-1 ms-4 mb-3">
                        <li>accurate financial records of all income and expenses.</li>
                        <li>paying authorized expenses in a prompt manner.</li>
                        <li>delivering invoices in a prompt manner for tournament entry fees and other services rendered directly by the corporation.</li>
                        <li>providing the membership with an annual report on the financial condition of the corporation.</li>
                    </ol>
                </li>
                <li>see that all notices are duly given in accordance with these bylaws or as required by law.</li>
                <li>assist the Director of Outreach in maintaining a list of quiz bowl programs in the state of Michigan along with contact information for their coaches or any other relevant points of contact</li>
            </ol>
            <h6 className="text-md font-semibold text-gray-100 mb-1">Section 9. Duties of Director of Outreach</h6>
            <div>The Director of Outreach shall coordinate the corporation's activities related to encouraging increased
                participation in high-quality quiz bowl, such as:</div>
            <ol type="a" className="list-[lower-alpha] list-inside mt-2 space-y-1 ms-4 mb-3">
                <li>identifying schools that have limited participation in high-quality quiz bowl events.</li>
                <li>developing activities for the organization to pursue to encourage greater participation in high-quality quiz bowl events.</li>
                <li>identifying potential hosts for events supported by the Michigan Quiz Bowl Association.</li>
                <li>advising hosts of supported events on best practices for successful quiz bowl events.</li>
                <li>maintain a list of quiz bowl programs in the state of Michigan along with contact information for their coaches or any other relevant points of contact</li>
            </ol>
            <h5 className="text-lg font-semibold text-gray-100 mb-1">Article V -- Meetings</h5>
            <h6 className="text-md font-semibold text-gray-100 mb-1">Section 1. Meeting Schedule</h6>
            <ol type="a" className="list-[lower-alpha] list-inside mt-2 space-y-1 ms-4 mb-3">
                <li>The corporation shall hold at least one meeting per year open to all active members.</li>
                <li>As determined by the Board of Directors, additional general meetings for all active members may be called
                    <ol type="a" className="list-[lower-alpha] list-inside mt-2 space-y-1 ms-4 mb-3">
                        <li>It is expected but not required that these meetings occur on a roughly 1/month cadence, with a break for the months of June and July</li>
                    </ol>
                </li>
                <li>The Board of Directors shall announce the time and location of these meetings to the active membership no less than ten days prior to the meeting.</li>
            </ol>
            <h6 className="text-md font-semibold text-gray-100 mb-1">Section 2. Special Meetings</h6>
            <ol type="a" className="list-[lower-alpha] list-inside mt-2 space-y-1 ms-4 mb-3">
                <li>The corporation shall hold a special meeting of members in any of the following cases:
                    <ol type="a" className="list-[lower-roman] list-inside mt-2 space-y-1 ms-4 mb-3">
                        <li>The meeting is called by the Board of Directors</li>
                        <li>At least five percent of active members deliver written notice to the Board of Directors calling for a meeting and describing the purposes for the meeting.</li>
                    </ol>
                </li>
                <li>The Board of Directors shall announce the time and location of the special meeting to the active membership no less than ten days and no more than sixty days prior to the meeting.</li>
            </ol>
            <h6 className="text-md font-semibold text-gray-100 mb-1">Section 3. Quorum and Voting</h6>
            <div>Unless otherwise specified by the bylaws:</div>
            <ol type="a" className="list-[lower-alpha] list-inside mt-2 space-y-1 ms-4 mb-3">
                <li>At least one-third of the active membership or a simple majority of the Board of Directors shall constitute a quorum for any votes conducted at a meeting of members.</li>
                <li>A motion voted on by active members present at a meeting passes if it receives more "Yes" votes than "No" votes.</li>
            </ol>
            <h5 className="text-lg font-semibold text-gray-100 mb-1">Article VI -- Committees</h5>
            <h6 className="text-md font-semibold text-gray-100 mb-1">Section 1. Designation and Purpose</h6>
            <div className="mb-1">The Board of Directors may designate regular or special committees to coordinate specific functions or
                projects of the corporation.</div>
            <h6 className="text-md font-semibold text-gray-100 mb-1">Section 2. Membership</h6>
            <ol type="a" className="list-[lower-alpha] list-inside mt-2 space-y-1 ms-4 mb-3">
                <li>The chairperson and membership of each committee shall be designated by the Board of Directors.</li>
                <li>Each committee shall have a minimum of three members.</li>
                <li>Each committee shall have at least one member from the Board of Directors.</li>
            </ol>
            <h6 className="text-md font-semibold text-gray-100 mb-1">Section 3. Meetings and Actions of Committees</h6>
            <ol type="a" className="list-[lower-alpha] list-inside mt-2 space-y-1 ms-4 mb-3">
                <li>Meetings of a committee may be called by its chairperson with at least five days' notice to all committee members.</li>
                <li>Meetings shall be open to all active members of the corporation regardless of membership in the committee.</li>
                <li>More than half of a committee's members must be present at any meeting.</li>
                <li>Each member of the committee is entitled to one vote on matters voted on by the committee. A motion voted on by committee members passes if more than half of the committee membership registers a "Yes", "No", or "Abstain" vote and the motion receives more "Yes" votes than "No" votes. For votes conducted at a meeting, voting members present at the meeting who do not register a vote for a particular motion will be considered to have voted "Abstain".</li>
                <li>All committees shall provide the Board of Directors minutes of all meetings and a summary of all actions taken by the committee within fifteen days of each meeting or action. The committee or Board of Directors shall make these reports available to the membership of the entire corporation.</li>
                <li>Committee business may be conducted without a formal meeting by email or other communication methods approved by the Board of Directors.</li>
            </ol>
            <h5 className="text-lg font-semibold text-gray-100 mb-1">Article VII -- Finances</h5>
            <h6 className="text-md font-semibold text-gray-100 mb-1">Section 1. Operating Year</h6>
            <div className="mb-1">The operating and fiscal year of the corporation shall be August 1 to July 31.</div>
            <h6 className="text-md font-semibold text-gray-100 mb-1">Section 2. Expenses</h6>
            <ol type="a" className="list-[lower-alpha] list-inside mt-2 space-y-1 ms-4 mb-3">
                <li>All expenses and reimbursements paid by the corporation shall be authorized by majority vote of the Board of Directors.</li>
                <li>The Board of Directors may authorize specific expenditures or a general budget for expenses for a particular activity.</li>
                <li>Members incurring expenses on behalf of the corporation shall not exceed any authorized dollar amount or use funds for any unauthorized purpose.</li>
            </ol>
            <h6 className="text-md font-semibold text-gray-100 mb-1">Section 3. Deposits</h6>
            <div className="mb-1">All funds of the corporation shall be deposited in a corporate bank account or accounts designated by the Board of Directors.</div>
            <h5 className="text-lg font-semibold text-gray-100 mb-1">Article VIII -- Amendments to Bylaws</h5>
            <h6 className="text-md font-semibold text-gray-100 mb-1">Section 1. Proposal</h6>
            <div className="mb-1">An amendment to these bylaws may be proposed by any active member. A proposed amendment shall be delivered to the Secretary with all additions and removals clearly indicated.</div>
            <h6 className="text-md font-semibold text-gray-100 mb-1">Section 2. Voting Procedure</h6>
            <ol type="a" className="list-[lower-alpha] list-inside mt-2 space-y-1 ms-4 mb-3">
                <li>Upon receipt of a proposed amendment, the Secretary shall deliver to all active members via email a notice of the proposed amendment with the text of the proposed changes. A seven-day period of discussion shall follow.</li>
                <li>Following the discussion period, the Secretary shall deliver to all active members via email a bylaw amendment ballot consisting of all amendments under consideration. Members shall have until 11:59 PM Eastern Time on the seventh day following the send date to cast as "Yes", "No", or "Abstain" vote for each proposed amendment.</li>
                <li>If all active members have cast a vote, the President or the Secretary may complete the voting period early.</li>
                <li>Following the voting period, if an amendment receives "Yes", "No", or "Abstain" votes from more than half of the active membership and at least twice as many "Yes" votes than "No" votes, that amendment shall be adopted.</li>
            </ol>
        </div>
    </InfoCard>
);

const Board = () => (
    <InfoCard title="Our Board" icon={Info}>
        <div className="text-xl font-semibold text-gray-100 mt-6 mb-0">President: Jimmy Dunn (DeWitt High School)</div>
        <div className="text-sm font-semibold text-gray-100 mt-0 mb-2">Term: April 2025 through July 2029</div>
        <p>
            Jimmy joined DeWitt High School's quiz bowl team in 8th grade, as there was not team at the Middle School at the time. As a player he was a part of DeWitt's 2008 Class B State Championship team and captained the 2009 QuizBusters Grand Championship team. He joined DeWitt as a member of the coaching staff in 2017, and has helped organize the Michigan High School Quiz Bowl State Championship each year since 2021.
        </p>
        <div className="text-xl font-semibold text-gray-100 mt-6 mb-0">Vice President: Christopher Gismondi (Detroit Catholic Central)</div>
        <div className="text-sm font-semibold text-gray-100 mt-0 mb-2">Term: April 2025 through July 2029</div>
        <p>
            <div>Christopher Gismondi began as an assistant coach at Catholic Central in 2011. In 2013 he took over as co-head coach of the program with Benjamin Herman. Together, their teams have won over 2,000 games, 140 tournaments, 13 state championships and 3 national championships.</div>
            <div>Since 2014, Chris and Ben also began organizing and hosting season opening middle school tournaments, middle school state championship tournaments, and quiz bowl camps.</div>
        </p>
        <div className="text-xl font-semibold text-gray-100 mt-6 mb-0">Secretary: Stephen Smith (White Cloud High School)</div>
        <div className="text-sm font-semibold text-gray-100 mt-0 mb-2">Term: April 2025 through July 2029</div>
        <p>
            <div>Serving as a Social Studies teacher at White Cloud High School since 2017, Stephen now coaches the Cloud's highly acclaimed quiz bowl program. Since taking command, he guided the team to the 2023 Class C/D State Championship, several CSAA Championships, and commendable finishes at the NAQT Small School National Championship Tournaments. In addition, he serves as the Tournament Director for White Cloud's Jan Rademacher Memorial Quiz Bowl Invitational, held annually in the Fall season.</div>
        </p>
        <div className="text-xl font-semibold text-gray-100 mt-6 mb-0">Laura Foreback (DeWitt Middle School)</div>
        <div className="text-sm font-semibold text-gray-100 mt-0 mb-2">Term: April 2025 through July 2029</div>
        <div className="text-xl font-semibold text-gray-100 mt-6 mb-0">Director of Outreach: Andrew Holm (Hillsdale Academy)</div>
        <div className="text-sm font-semibold text-gray-100 mt-0 mb-2">Term: April 2025 through July 2029</div>
    </InfoCard>
);

const MIQBAFormat = () => {
    return (
        <InfoCard title="MIQBA Format" icon={BriefcaseBusiness}>
            <h4>What MIQBA Format means</h4>
            <h5>Tournaments that have been certified as MIQBA Format have elevated qualification benefits for the Michigan State Championship</h5>
            <ol type="a" className="list-[lower-alpha] list-inside mt-2 space-y-1 ms-4 mb-3">
                <li>
                    A MIQBA Format competition taking place in Michigan or online hosted by a Michigan host will have the top number of Varsity teams from Michigan-based schools equal to 20% of the field size, rounded up, qualify their school for the State Championship, allowing all teams that are tied for the last qualifying spot to qualify.
                    <ol type="a" className="list-[lower-roman] list-inside mt-2 space-y-1 ms-4 mb-3">
                        <li>Non-Michigan based schools still count towards field size, but are ineligible for the state championship and will be omitted from the 20%</li>
                        <li>High School Non-Varsity divisions will still count towards field size, but are ineligible to qualify their school for the state championship</li>
                    </ol>
                </li>
                <li>
                    If teams from at least three distinct schools of a given class category are present, the top team from that class size will qualify for the state championship regardless of their placement in the overall field.
                    <ol type="a" className="list-[lower-roman] list-inside mt-2 space-y-1 ms-4 mb-3">
                        <li>The three categories are defined presently as MHSAA Class A, MHSAA Class B, and MHSAA Class C/D</li>
                    </ol>
                </li>
                <li>
                    To have a competition certified as “MIQBA Format”, a league or tournament director should reach out to MIQBA at miqbaorg@gmail.com and submit the details of their competition
                    <ol type="a" className="list-[lower-roman] list-inside mt-2 space-y-1 ms-4 mb-3">
                        <li>A simple majority vote via e-mail of the Board of Directors will be sufficient to confirm a competition as MIQBA Format</li>
                    </ol>
                </li>
                <li>A MIQBA Format competition must have at least 6 rounds of games, with every team guaranteed to play at least 5 games</li>
                <li>A MIQBA Format competition must have at least 3 schools represented and at least 50% of teams must be from Michigan-based schools</li>
                <li>A MIQBA Format competition will have every played game consist of two teams with at most 4 players per team playing at any given time</li>
                <li>
                    A MIQBA Format competition must use questions provided by a reputable question provider, with pyramidal questions in a tossup/bonus cycle format.
                    <ol type="a" className="list-[lower-roman] list-inside mt-2 space-y-1 ms-4 mb-3">
                        <li>
                            The determination of what constitutes a “reputable” question provider will be at the sole discretion of the Board of Directors. A list of question providers may be published on the MIQBA website.
                        </li>
                        <li>
                            Additional scoring rounds such as “Lightning Rounds” or similar worksheet-based rounds are generally considered acceptable to include.
                        </li>
                    </ol>
                </li>
                <li>A MIQBA Format competition must have either a minimum of 16 tossup/bonus cycles in an untimed match, or a minimum of 8-minute halves for a timed match. It is emphatically recommended that competitions have 20 tossup/bonus cycles or at least 10-minute halves.</li>
                <li>
                    A MIQBA Format competition is strongly recommended, but not required at this time, to report scoring details consisting of
                    <ol type="a" className="list-[lower-roman] list-inside mt-2 space-y-1 ms-4 mb-3">
                        <li>
                            Game by game scores with number of points from tossups and number of points from bonuses
                        </li>
                        <li>
                            Individual scores by players on a game-by-game basis
                        </li>
                    </ol>
                </li>
                <li>MIQBA recommends using Yellowfruit on Windows for competitions that keep score on paper, and TJSheets for competitions that keep score digitally. Please reach out with any questions.</li>
            </ol>
        </InfoCard>
    );
};

const Schedule = () => {
    return (
        <InfoCard title="Tournament Schedule" icon={Calendar}>
            <h4>View upcoming tournaments on the MIQBA Google Calendar.</h4>
            <h4>Tournaments marked with an asterisk are certified as MIQBA Format and have elevated qualification benefits for the Michigan State Championship</h4>
            <div className="flex flex-col">
                <div className="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                    <div className="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
                        <div className="shadow overflow-hidden border-b border-gray-700 sm:rounded-lg">
                            <iframe
                                src="https://calendar.google.com/calendar/embed?height=600&amp;wkst=1&amp;ctz=America%2FNew_York&amp;mode=AGENDA&amp;src=bWlxYmFvcmdAZ21haWwuY29t&amp;color=%23039be5"
                                width="800"
                                height="600"
                                frameBorder="0"
                                scrolling="no"
                                title="Michigan Quiz Bowl Event Calendar">
                            </iframe>
                        </div>
                    </div>
                </div>
            </div>
        </InfoCard>
    );
};

const CoachResources = () => (
    <div className="space-y-8">
        <InfoCard title="Coach Resources" icon={Users}>
            <p className="text-gray-400">Resources for coaches building and running a quiz bowl program.</p>
        </InfoCard>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <ResourceSection
                title="Starting a Team"
                description="Interested in starting a quiz bowl team at your school? You'll need a faculty advisor, a few interested students, and a space to practice."
                icon={Users}
            >
                <ResourceLink href="https://www.naqt.com/how-to/start-a-team.jsp">
                    Read the NAQT Guide to Starting a Team
                </ResourceLink>
            </ResourceSection>
            <ResourceSection
                title="MIQBA Guides"
                description="Presentations and notes from MIQBA coach development meetings."
                icon={FileText}
            >
                <ResourceLink href="https://drive.google.com/file/d/1gfn9iHp3ySAv5Iglkh1bYSz3KSTsjn2S/view?usp=drive_link">
                    Improving Your Team — The Presentation (Jan 2026)
                </ResourceLink>
                <ResourceLink href="https://drive.google.com/file/d/10rqMg8aBS97biotpBReGsL-kH3Lf7J_p/view?usp=drive_link">
                    Improving Your Team — The Notes (Jan 2026)
                </ResourceLink>
                <ResourceLink href="https://drive.google.com/file/d/171YC1gv_Zon86qTSnOtAX7EYNHkCQSGr/view?usp=sharing">
                    Running a Practice — The Presentation (Apr 2026)
                </ResourceLink>
            </ResourceSection>
            <ResourceSection
                title="Strategy & Meta"
                description="Great resources to share with your players or read yourself to better understand competitive quiz bowl."
                icon={BookOpen}
            >
                <ResourceLink href="https://dekidder.substack.net/">
                    "Holed Up With a Buzzer", Dwight Kidder's Substack
                </ResourceLink>
                <ResourceLink href="https://www.amazon.com/99-Critical-Shots-Quiz-Bowl-ebook/dp/B075Q12YWT/">
                    "The 99 Critical Shots in Quiz Bowl", Dwight Kidder's book
                </ResourceLink>
            </ResourceSection>
            <ResourceSection
                title="Reference Links"
                description="Helpful references for new and experienced coaches alike."
                icon={Info}
            >
                <ResourceLink href="https://www.naqt.com/rules/">
                    Official NAQT Rules
                </ResourceLink>
                <ResourceLink href="https://www.naqt.com/hs/distribution.jsp">
                    NAQT's Category Distribution for HS sets
                </ResourceLink>
                <ResourceLink href="https://hsquizbowl.org/db/">
                    HS Quiz Bowl Resource Center
                </ResourceLink>
                <ResourceLink href="https://www.pace-nsc.org/resources/">
                    PACE's Resources for Coaches and Players
                </ResourceLink>
            </ResourceSection>
        </div>
    </div>
);

const PlayerResources = ({ setCurrentPage }) => (
    <div className="space-y-8">
        <InfoCard title="Player Resources" icon={User}>
            <p className="text-gray-400">Resources for competitors learning how to play and improve in games.</p>
        </InfoCard>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <ResourceSection
                title="How to Play"
                description="Start here to learn the rules and fundamentals of competitive quiz bowl."
                icon={Info}
            >
                <ResourceLink href="https://www.naqt.com/rules/">
                    Official NAQT Rules
                </ResourceLink>
                <ResourceLink href="https://www.pace-nsc.org/resources/">
                    PACE's Resources for Coaches and Players
                </ResourceLink>
            </ResourceSection>
            <ResourceSection
                title="Strategy & Meta"
                description="Improve your buzzing, teamwork, and tournament performance."
                icon={BookOpen}
            >
                <ResourceLink href="https://dekidder.substack.net/">
                    "Holed Up With a Buzzer", Dwight Kidder's Substack
                </ResourceLink>
                <ResourceLink href="https://www.amazon.com/99-Critical-Shots-Quiz-Bowl-ebook/dp/B075Q12YWT/">
                    "The 99 Critical Shots in Quiz Bowl", Dwight Kidder's book
                </ResourceLink>
            </ResourceSection>
            <ResourceSection
                title="Practice Tools"
                description="Run practice rounds with your teammates and get reps on real questions."
                icon={User}
            >
                <ResourceLink href="https://www.qbreader.org/">
                    QBReader — online question reader and database
                </ResourceLink>
                <ResourceLink href="https://www.quizbowlpackets.com/">
                    Quiz Bowl Packet Archive — freely available practice packets
                </ResourceLink>
            </ResourceSection>
        </div>
        <ResourceCallout>
            Looking to build your knowledge base?{' '}
            <button
                onClick={() => setCurrentPage('StudyResources')}
                className="text-blue-400 font-semibold hover:text-blue-300 transition duration-150"
            >
                Study Resources &rarr;
            </button>
        </ResourceCallout>
    </div>
);

const StudyResources = ({ setCurrentPage }) => (
    <div className="space-y-8">
        <InfoCard title="Study Resources" icon={BookOpen}>
            <p className="text-gray-400">Resources for learning and retaining quiz bowl knowledge.</p>
        </InfoCard>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <ResourceSection
                title="Study Lists & Guides"
                description="Curated lists and guides for high-yield topics that come up in competition."
                icon={BookOpen}
            >
                <ResourceLink href="https://www.naqt.com/you-gotta-know/by-category.jsp">
                    NAQT's "You Gotta Know" lists
                </ResourceLink>
                <ResourceLink href="https://www.rockfordqb.com/study">
                    Rockford Quiz Bowl Company's Study Guides
                </ResourceLink>
                <ResourceLink href="https://www.naqt.com/hs/distribution.jsp">
                    NAQT's Category Distribution for HS sets
                </ResourceLink>
                <ResourceLink href="https://www.qbreader.org/db/frequency-list/">
                    QBReader's frequency lists
                </ResourceLink>
            </ResourceSection>
            <ResourceSection
                title="Flashcard Tools"
                description="Simple and powerful tools for memorizing quiz bowl content."
                icon={FileText}
            >
                <ResourceLink href="https://www.quizlet.com/">
                    Quizlet (Simple Flashcards)
                </ResourceLink>
                <ResourceLink href="https://www.knowt.com/">
                    Knowt (Simple Flashcards)
                </ResourceLink>
                <ResourceLink href="https://apps.ankiweb.net/">
                    Anki (Spaced repetition flashcards)
                </ResourceLink>
            </ResourceSection>
            <ResourceSection
                title="Study from Past Questions"
                description="Review old tournament packets to learn answer lines and see what topics appear in real games."
                icon={ClipboardList}
            >
                <ResourceLink href="https://www.quizbowlpackets.com/">
                    Quiz Bowl Packet Archive
                </ResourceLink>
            </ResourceSection>
        </div>
        <ResourceCallout>
            Ready to practice buzzing with teammates?{' '}
            <button
                onClick={() => setCurrentPage('PlayerResources')}
                className="text-blue-400 font-semibold hover:text-blue-300 transition duration-150"
            >
                Player Resources &rarr;
            </button>
        </ResourceCallout>
    </div>
);

const ScheduleGeneratorPage = ({ setCurrentPage }) => {
    const iframeRef = useRef(null);
    const resizeCleanupRef = useRef(null);
    const [iframeHeight, setIframeHeight] = useState(800);

    const setupIframeResize = () => {
        if (resizeCleanupRef.current) {
            resizeCleanupRef.current();
            resizeCleanupRef.current = null;
        }
        const iframe = iframeRef.current;
        const doc = iframe?.contentDocument;
        if (!doc?.documentElement) {
            return;
        }
        const updateHeight = () => {
            setIframeHeight(Math.max(doc.documentElement.scrollHeight, window.innerHeight * 0.8));
        };
        updateHeight();
        const observer = new ResizeObserver(updateHeight);
        observer.observe(doc.documentElement);
        resizeCleanupRef.current = () => observer.disconnect();
    };

    useEffect(() => () => {
        if (resizeCleanupRef.current) {
            resizeCleanupRef.current();
        }
    }, []);

    return (
        <div className="space-y-6">
            <InfoCard title="MIQBA Schedule Generator" icon={CalendarRange}>
                <p className="text-gray-400">
                    Build printable schedules, team itineraries, and room sheets from Conor&apos;s tournament formats.
                </p>
                <div className="flex flex-wrap gap-4 mt-4 text-sm">
                    <button
                        type="button"
                        className="text-blue-400 hover:underline"
                        onClick={() => setCurrentPage('TournamentDirectorResources')}
                    >
                        &larr; Back to Run a Tournament
                    </button>
                    <a
                        href="schedule-generator/index.html"
                        target="_blank"
                        rel="noreferrer"
                        className="text-blue-400 hover:underline inline-flex items-center gap-1"
                    >
                        Open full screen
                        <ExternalLink size={14} />
                    </a>
                </div>
            </InfoCard>
            <div className="rounded-lg border border-gray-700 overflow-hidden bg-white">
                <iframe
                    ref={iframeRef}
                    title="MIQBA Schedule Generator"
                    src="schedule-generator/index.html"
                    className="w-full border-0 min-h-[80vh]"
                    style={{ height: iframeHeight }}
                    onLoad={setupIframeResize}
                />
            </div>
        </div>
    );
};

const TournamentDirectorResources = ({ setCurrentPage }) => (
    <div className="space-y-8">
        <InfoCard title="Run a Tournament" icon={ClipboardList}>
            <p className="text-gray-400">Resources for running tournaments and leagues.</p>
        </InfoCard>
        <ResourceCallout>
            <h3 className="text-lg font-semibold text-white mb-2">MIQBA Schedule Generator</h3>
            <p className="mb-4">
                Build printable schedules, team itineraries, and room sheets from Conor&apos;s tournament formats.
            </p>
            <button
                type="button"
                className="text-blue-400 hover:underline font-medium"
                onClick={() => setCurrentPage('ScheduleGenerator')}
            >
                Open the Schedule Generator &rarr;
            </button>
        </ResourceCallout>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <ResourceSection
                title="MIQBA Guide — Running a Tournament or League"
                description="Presentation from the March 2026 MIQBA meeting."
                icon={FileText}
            >
                <ResourceLink href="https://docs.google.com/presentation/d/1AJPf6aQ560XRMka9cJxJ_fmnu7mP1uyFrjGm0-E8OAw/edit?usp=sharing">
                    The Presentation
                </ResourceLink>
            </ResourceSection>
            <ResourceSection
                title="Tournament Tools"
                description="Helpful tools for running a tournament or league."
                icon={ClipboardList}
            >
                <ResourceLink href="https://drive.google.com/drive/folders/1KyTY9RRZHAcQ4YMQCQFrMMGseJVLppuE?usp=sharing">
                    Conor's Tournament Formats
                </ResourceLink>
                <ResourceLink href="https://hdwhite.org/qb/schedules/">
                    HDWhite Schedule Generator
                </ResourceLink>
                <ResourceLink href="https://y.yellowfruit.app/">
                    YellowFruit
                </ResourceLink>
                <ResourceLink href="https://docs.google.com/spreadsheets/d/1w7srkeg-mEImLIhwgLQSw2IHL_3idUYx4VBLsslrxUg/edit#gid=2081521394">
                    TJSheets Control Sheet
                </ResourceLink>
                <ResourceLink href="https://docs.google.com/spreadsheets/d/1IJji0c23_mOYz6SnMiUlj2WDCIAHutIqfMdG-0hpEIU/edit#gid=1477353732">
                    TJSheets Scoresheet Template
                </ResourceLink>
            </ResourceSection>
        </div>
    </div>
);

const Contact = () => (
    <InfoCard title="Contact Us" icon={Mail}>
        <p className="mb-6">
            Have questions? We'd love to answer them.
        </p>

        <div className="space-y-4">
            <div>
                <h4 className="text-lg font-semibold text-gray-100">General Inquiries</h4>
                <p>For any general questions about MIQBA, events, or getting involved.</p>
                <a href="mailto:miqbaorg@gmail.com" className="text-blue-400 hover:underline">miqbaorg@gmail.com</a>
            </div>
        </div>
        <div className="space-y-4">
            <div>
                <h4 className="text-lg font-semibold text-gray-100">Coach Groups</h4>
                <a href="https://www.facebook.com/groups/808061864589687" className="text-blue-400 hover:underline">Join the Facebook group!</a>
            </div>
        </div>
    </InfoCard>
);

const HISTORY_ERAS = [
    { id: 'merger', label: 'Merger Era (2021–Present)' },
    { id: 'open', label: 'Open Era (2000–2020)' },
    { id: 'honors', label: 'Honors College (1988-2019)' },
];

const HISTORY_CLASSES = [
    { id: 'classA', label: 'Class A' },
    { id: 'classB', label: 'Class B' },
    { id: 'classCD', label: 'Class C/D' },
];

const HISTORY_INDIVIDUAL_CLASSES = [
    { id: 'all', label: 'All Classes' },
    ...HISTORY_CLASSES,
];

const HISTORY_INDIVIDUAL_YEARS = [2026, 2025, 2024, 2023, 2022, 2021];

const HISTORY_MODES = [
    { id: 'era', label: 'By Era' },
    { id: 'school', label: 'By School' },
    { id: 'individuals', label: 'Individuals' },
];

const SCHOOL_NAME_ALIASES = {
    'Detroit Jesuit': 'U of D Jesuit',
};

const isMutedHistoryValue = (value) =>
    !value
    || value.startsWith('No ')
    || value.startsWith('Cancelled')
    || value === 'Not listed';

const getSchoolGroupKey = (name) => {
    const stripped = name.replace(/\s+[A-D]$/, '').trim();
    return SCHOOL_NAME_ALIASES[stripped] ?? stripped;
};

const splitPlacementNames = (value) => {
    if (isMutedHistoryValue(value)) {
        return [];
    }
    return value
        .replace(/\s*\(tie\)\s*/gi, '')
        .split(/[,/]/)
        .map((part) => part.trim())
        .filter(Boolean);
};

const addYearlyPlacements = (placements, schoolKeys, rows, eraLabel, classLabel) => {
    const placementFields = [
        ['champion', 'Champion'],
        ['runnerUp', 'Runner-Up'],
        ['thirdPlace', 'Third Place'],
    ];

    rows.forEach((row) => {
        placementFields.forEach(([field, placement]) => {
            if (!row[field]) {
                return;
            }
            splitPlacementNames(row[field]).forEach((teamName) => {
                const schoolKey = getSchoolGroupKey(teamName);
                schoolKeys.add(schoolKey);
                placements.push({
                    schoolKey,
                    teamName,
                    year: row.year,
                    eraLabel,
                    classLabel,
                    placement,
                });
            });
        });
    });
};

const buildSchoolHistoryIndex = (data, individualData) => {
    const schoolKeys = new Set();
    const placements = [];
    const individuals = [];

    HISTORY_CLASSES.forEach(({ id, label }) => {
        addYearlyPlacements(
            placements,
            schoolKeys,
            data.honorsCollege.yearly[id],
            'Honors College (1988-2019)',
            label
        );
    });
    addYearlyPlacements(
        placements,
        schoolKeys,
        data.openEra.yearly,
        'Open Era (2000-2020)',
        'Combined'
    );
    HISTORY_CLASSES.forEach(({ id, label }) => {
        addYearlyPlacements(
            placements,
            schoolKeys,
            data.mergerEra.yearly[id],
            'Merger Era (2021–Present)',
            label
        );
    });

    HISTORY_CLASSES.forEach(({ id, label }) => {
        (individualData.mergerEra[id] || []).forEach((row) => {
            const schoolKey = getSchoolGroupKey(row.school);
            schoolKeys.add(schoolKey);
            individuals.push({
                schoolKey,
                school: row.school,
                player: row.player,
                year: row.year,
                classLabel: label,
                rank: row.rank,
                pp20tuh: row.pp20tuh,
            });
        });
    });

    return {
        schools: [...schoolKeys].sort((a, b) => a.localeCompare(b)),
        placements,
        individuals,
    };
};

const schoolHistoryIndex = buildSchoolHistoryIndex(historyData, individualHistoryData);

const HISTORY_SCHOOL_LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

const getSchoolsByLetter = (schools) => {
    const byLetter = Object.fromEntries(HISTORY_SCHOOL_LETTERS.map((letter) => [letter, []]));
    for (const school of schools) {
        const letter = school.charAt(0).toUpperCase();
        if (byLetter[letter]) {
            byLetter[letter].push(school);
        }
    }
    return byLetter;
};

const historyFilterButtonClass = (isActive) =>
    `px-3 py-2 rounded-md text-sm font-medium transition-colors duration-150
    ${isActive
        ? 'text-white bg-blue-600'
        : 'text-gray-300 hover:text-white hover:bg-gray-700'
    }`;

const historyFilterButtonDisabledClass =
    'px-3 py-2 rounded-md text-sm font-medium text-gray-600 bg-gray-800/40 cursor-not-allowed';

const HistoryCell = ({ value, emphasize = false }) => (
    <td className={`px-4 py-3 text-sm ${isMutedHistoryValue(value) ? 'text-gray-500 italic' : emphasize ? 'text-white font-medium' : 'text-gray-300'}`}>
        {value}
    </td>
);

const HistoryFilterBar = ({
    mode,
    onModeChange,
    era,
    setEra,
    view,
    setView,
    classSize,
    setClassSize,
    search,
    setSearch,
    selectedSchool,
    setSelectedSchool,
    schoolOptions,
    individualYear,
    setIndividualYear,
}) => {
    const [schoolLetter, setSchoolLetter] = useState('');
    const schoolsByLetter = useMemo(() => getSchoolsByLetter(schoolOptions), [schoolOptions]);
    const showClassFilter = mode === 'individuals' || (mode === 'era' && era !== 'open');

    useEffect(() => {
        if (mode !== 'school') {
            setSchoolLetter('');
        }
    }, [mode]);

    return (
        <div className="bg-gray-800/60 rounded-lg border border-gray-700 p-5 space-y-4">
            <div className="flex flex-wrap gap-2">
                {HISTORY_MODES.map(({ id, label }) => (
                    <button
                        key={id}
                        type="button"
                        onClick={() => onModeChange(id)}
                        className={historyFilterButtonClass(mode === id)}
                    >
                        {label}
                    </button>
                ))}
            </div>
            {mode === 'era' && (
                <>
                    <div className="flex flex-wrap gap-2">
                        {HISTORY_ERAS.map(({ id, label }) => (
                            <button
                                key={id}
                                type="button"
                                onClick={() => setEra(id)}
                                className={historyFilterButtonClass(era === id)}
                            >
                                {label}
                            </button>
                        ))}
                    </div>
                    <div className="flex flex-wrap gap-2">
                        <button
                            type="button"
                            onClick={() => setView('yearly')}
                            className={historyFilterButtonClass(view === 'yearly')}
                        >
                            Year-by-Year
                        </button>
                        <button
                            type="button"
                            onClick={() => setView('records')}
                            className={historyFilterButtonClass(view === 'records')}
                        >
                            All-Time Records
                        </button>
                    </div>
                    {showClassFilter && (
                        <div className="flex flex-wrap gap-2">
                            {HISTORY_CLASSES.map(({ id, label }) => (
                                <button
                                    key={id}
                                    type="button"
                                    onClick={() => setClassSize(id)}
                                    className={historyFilterButtonClass(classSize === id)}
                                >
                                    {label}
                                </button>
                            ))}
                        </div>
                    )}
                    <div>
                        <label htmlFor="history-search" className="sr-only">Search schools</label>
                        <input
                            id="history-search"
                            type="search"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Search by school name..."
                            className="w-full md:max-w-md px-4 py-2 rounded-md bg-gray-900/60 border border-gray-700 text-gray-200 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                    </div>
                </>
            )}
            {mode === 'school' && (
                <div className="space-y-4">
                    <div>
                        <label htmlFor="history-school-select" className="sr-only">Select school</label>
                        <input
                            id="history-school-select"
                            type="search"
                            list="history-school-options"
                            value={selectedSchool}
                            onChange={(e) => setSelectedSchool(e.target.value)}
                            placeholder="Search and select a school..."
                            className="w-full md:max-w-md px-4 py-2 rounded-md bg-gray-900/60 border border-gray-700 text-gray-200 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <datalist id="history-school-options">
                            {schoolOptions.map((school) => (
                                <option key={school} value={school} />
                            ))}
                        </datalist>
                    </div>
                    <div className="flex flex-wrap gap-1">
                        {HISTORY_SCHOOL_LETTERS.map((letter) => {
                            const hasSchools = schoolsByLetter[letter].length > 0;
                            return (
                                <button
                                    key={letter}
                                    type="button"
                                    disabled={!hasSchools}
                                    onClick={() => setSchoolLetter(letter)}
                                    className={hasSchools
                                        ? historyFilterButtonClass(schoolLetter === letter)
                                        : historyFilterButtonDisabledClass
                                    }
                                    aria-pressed={hasSchools ? schoolLetter === letter : undefined}
                                >
                                    {letter}
                                </button>
                            );
                        })}
                    </div>
                    {schoolLetter && (
                        <div className="flex flex-wrap gap-2">
                            {schoolsByLetter[schoolLetter].map((school) => (
                                <button
                                    key={school}
                                    type="button"
                                    onClick={() => setSelectedSchool(school)}
                                    className={historyFilterButtonClass(resolveSchoolKey(selectedSchool, schoolOptions) === school)}
                                >
                                    {school}
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            )}
            {mode === 'individuals' && (
                <>
                    <div className="flex flex-wrap gap-2">
                        {HISTORY_INDIVIDUAL_CLASSES.map(({ id, label }) => (
                            <button
                                key={id}
                                type="button"
                                onClick={() => setClassSize(id)}
                                className={historyFilterButtonClass(classSize === id)}
                            >
                                {label}
                            </button>
                        ))}
                    </div>
                    <div className="flex flex-wrap gap-2">
                        <button
                            type="button"
                            onClick={() => setIndividualYear(null)}
                            className={historyFilterButtonClass(individualYear === null)}
                        >
                            All Years
                        </button>
                        {HISTORY_INDIVIDUAL_YEARS.map((year) => (
                            <button
                                key={year}
                                type="button"
                                onClick={() => setIndividualYear(year)}
                                className={historyFilterButtonClass(individualYear === year)}
                            >
                                {year}
                            </button>
                        ))}
                    </div>
                    <p className="text-sm text-gray-400">
                        {individualYear != null
                            ? `Individual performances in ${individualYear}, ranked by points per 20 tossups heard (PP20TUH) within the selected class.`
                            : 'Top 50 individual performances at the Merger Era state championships (2021–Present), ranked by points per 20 tossups heard (PP20TUH).'}
                    </p>
                    <div>
                        <label htmlFor="history-individual-search" className="sr-only">Search individual records</label>
                        <input
                            id="history-individual-search"
                            type="search"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Search by player or school..."
                            className="w-full md:max-w-md px-4 py-2 rounded-md bg-gray-900/60 border border-gray-700 text-gray-200 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                    </div>
                </>
            )}
        </div>
    );
};

const HistoryYearlyTable = ({ rows, showThirdPlace }) => {
    if (rows.length === 0) {
        return <p className="text-gray-400 text-center py-8">No results match your search.</p>;
    }

    return (
        <div className="overflow-x-auto rounded-lg border border-gray-700">
            <table className="min-w-full divide-y divide-gray-700">
                <thead className="bg-gray-800 sticky top-0">
                    <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-400">Year</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-400">Champion</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-400">Runner-Up</th>
                        {showThirdPlace && (
                            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-400">Third Place</th>
                        )}
                    </tr>
                </thead>
                <tbody className="bg-gray-900/40 divide-y divide-gray-700/60">
                    {rows.map((row) => (
                        <tr key={row.year} className="hover:bg-gray-700/30 transition-colors duration-150">
                            <td className="px-4 py-3 text-sm text-gray-400 font-medium">{row.year}</td>
                            <HistoryCell value={row.champion} emphasize />
                            <HistoryCell value={row.runnerUp} />
                            {showThirdPlace && <HistoryCell value={row.thirdPlace} />}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

const HistoryIndividualTable = ({ rows, showClass, showOverallRank }) => {
    if (rows.length === 0) {
        return <p className="text-gray-400 text-center py-8">No results match your search.</p>;
    }

    return (
        <div className="overflow-x-auto rounded-lg border border-gray-700">
            <table className="min-w-full divide-y divide-gray-700">
                <thead className="bg-gray-800 sticky top-0">
                    <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-400">Rank</th>
                        {showOverallRank && (
                            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-400">Overall Rank</th>
                        )}
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-400">Year</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-400">Player</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-400">School</th>
                        {showClass && (
                            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-400">Class</th>
                        )}
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-400">PP20TUH</th>
                    </tr>
                </thead>
                <tbody className="bg-gray-900/40 divide-y divide-gray-700/60">
                    {rows.map((row) => (
                        <tr
                            key={`${row.rank}-${row.year}-${row.player}-${row.classLabel || ''}`}
                            className="hover:bg-gray-700/30 transition-colors duration-150"
                        >
                            <td className="px-4 py-3 text-sm text-gray-400 font-medium">{row.rank}</td>
                            {showOverallRank && (
                                <td className="px-4 py-3 text-sm text-gray-400 font-medium">{row.overallRank}</td>
                            )}
                            <td className="px-4 py-3 text-sm text-gray-400 font-medium">{row.year}</td>
                            <HistoryCell value={row.player} emphasize />
                            <HistoryCell value={row.school} />
                            {showClass && <HistoryCell value={row.classLabel} />}
                            <td className="px-4 py-3 text-sm text-gray-300">{row.pp20tuh.toFixed(2)}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

const HistoryRecordsTable = ({ rows, columns }) => {
    if (rows.length === 0) {
        return <p className="text-gray-400 text-center py-8">No results match your search.</p>;
    }

    return (
        <div className="overflow-x-auto rounded-lg border border-gray-700">
            <table className="min-w-full divide-y divide-gray-700">
                <thead className="bg-gray-800 sticky top-0">
                    <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-400">School</th>
                        {columns.map(({ key, label }) => (
                            <th key={key} className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-400">{label}</th>
                        ))}
                    </tr>
                </thead>
                <tbody className="bg-gray-900/40 divide-y divide-gray-700/60">
                    {rows.map((row) => (
                        <tr key={row.school} className="hover:bg-gray-700/30 transition-colors duration-150">
                            <HistoryCell value={row.school} emphasize />
                            {columns.map(({ key }) => (
                                <td key={key} className="px-4 py-3 text-sm text-gray-300">{row[key]}</td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

const resolveSchoolKey = (query, schools) => {
    const trimmed = query.trim();
    if (!trimmed) {
        return '';
    }
    const groupKey = getSchoolGroupKey(trimmed);
    if (schools.includes(groupKey)) {
        return groupKey;
    }
    return schools.find((school) => school.toLowerCase() === trimmed.toLowerCase()) || '';
};

const HistorySchoolTable = ({ placements, individuals, selectedSchool }) => {
    const resolvedSchoolKey = resolveSchoolKey(selectedSchool, schoolHistoryIndex.schools);

    if (!resolvedSchoolKey) {
        return <p className="text-gray-400 text-center py-8">Search and select a school to view results.</p>;
    }

    const rows = placements.filter((row) => row.schoolKey === resolvedSchoolKey);
    const individualRows = individuals
        .filter((row) => row.schoolKey === resolvedSchoolKey)
        .sort((a, b) => b.year - a.year || b.pp20tuh - a.pp20tuh);

    if (rows.length === 0 && individualRows.length === 0) {
        return <p className="text-gray-400 text-center py-8">No results found for this school.</p>;
    }

    const championships = rows.filter((row) => row.placement === 'Champion').length;
    const runnerUps = rows.filter((row) => row.placement === 'Runner-Up').length;
    const thirdPlaces = rows.filter((row) => row.placement === 'Third Place').length;

    return (
        <div className="space-y-8">
            <div className="space-y-4">
                <p className="text-gray-300 text-sm">
                    <span className="text-white font-semibold">{resolvedSchoolKey}</span>
                    {rows.length > 0 && (
                        <>
                            {' — '}
                            {championships} championship{championships === 1 ? '' : 's'}
                            {' · '}
                            {runnerUps} runner-up
                            {' · '}
                            {thirdPlaces} third place
                        </>
                    )}
                </p>
                {rows.length > 0 ? (
                    <div className="overflow-x-auto rounded-lg border border-gray-700">
                        <table className="min-w-full divide-y divide-gray-700">
                            <thead className="bg-gray-800 sticky top-0">
                                <tr>
                                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-400">Year</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-400">Era</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-400">Class</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-400">Placement</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-400">Team</th>
                                </tr>
                            </thead>
                            <tbody className="bg-gray-900/40 divide-y divide-gray-700/60">
                                {rows.map((row) => (
                                    <tr
                                        key={`${row.year}-${row.eraLabel}-${row.classLabel}-${row.placement}-${row.teamName}`}
                                        className="hover:bg-gray-700/30 transition-colors duration-150"
                                    >
                                        <td className="px-4 py-3 text-sm text-gray-400 font-medium">{row.year}</td>
                                        <HistoryCell value={row.eraLabel} />
                                        <HistoryCell value={row.classLabel} />
                                        <HistoryCell value={row.placement} emphasize={row.placement === 'Champion'} />
                                        <HistoryCell value={row.teamName !== row.schoolKey ? row.teamName : '—'} />
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <p className="text-gray-500 text-sm italic">No team podium finishes on record.</p>
                )}
            </div>
            {individualRows.length > 0 && (
                <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-100">Top Individual Performances (Merger Era)</h3>
                    <div className="overflow-x-auto rounded-lg border border-gray-700">
                        <table className="min-w-full divide-y divide-gray-700">
                            <thead className="bg-gray-800 sticky top-0">
                                <tr>
                                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-400">Year</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-400">Class</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-400">Player</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-400">Rank</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-400">PP20TUH</th>
                                </tr>
                            </thead>
                            <tbody className="bg-gray-900/40 divide-y divide-gray-700/60">
                                {individualRows.map((row) => (
                                    <tr
                                        key={`${row.year}-${row.classLabel}-${row.player}-${row.rank}`}
                                        className="hover:bg-gray-700/30 transition-colors duration-150"
                                    >
                                        <td className="px-4 py-3 text-sm text-gray-400 font-medium">{row.year}</td>
                                        <HistoryCell value={row.classLabel} />
                                        <HistoryCell value={row.player} emphasize />
                                        <td className="px-4 py-3 text-sm text-gray-300">{row.rank}</td>
                                        <td className="px-4 py-3 text-sm text-gray-300">{row.pp20tuh.toFixed(2)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
};

const getHistoryRows = (era, view, classSize) => {
    if (era === 'honors') {
        return view === 'yearly'
            ? historyData.honorsCollege.yearly[classSize]
            : historyData.honorsCollege.records[classSize];
    }
    if (era === 'open') {
        return view === 'yearly'
            ? historyData.openEra.yearly
            : historyData.openEra.records;
    }
    return view === 'yearly'
        ? historyData.mergerEra.yearly[classSize]
        : historyData.mergerEra.records[classSize];
};

const filterYearlyRows = (rows, search) => {
    const query = search.trim().toLowerCase();
    if (!query) {
        return [...rows].sort((a, b) => b.year - a.year);
    }
    return rows
        .filter((row) =>
            [row.champion, row.runnerUp, row.thirdPlace]
                .filter(Boolean)
                .some((value) => value.toLowerCase().includes(query))
        )
        .sort((a, b) => b.year - a.year);
};

const filterRecordRows = (rows, search) => {
    const query = search.trim().toLowerCase();
    const sorted = [...rows].sort((a, b) => b.championships - a.championships || a.school.localeCompare(b.school));
    if (!query) {
        return sorted;
    }
    return sorted.filter((row) => row.school.toLowerCase().includes(query));
};

const rankIndividualRows = (rows) =>
    [...rows]
        .sort((a, b) => b.pp20tuh - a.pp20tuh)
        .map((row, index) => ({ ...row, rank: index + 1 }));

const buildCombinedIndividualRows = (mergerEra) => {
    const combined = [];
    HISTORY_CLASSES.forEach(({ id, label }) => {
        (mergerEra[id] || []).forEach((row) => {
            combined.push({ ...row, classLabel: label });
        });
    });
    return rankIndividualRows(combined);
};

const getIndividualPerformanceKey = (row) =>
    `${row.year}|${row.player}|${row.school}`;

const buildOverallRankByPerformanceKey = (combinedRows) => {
    const map = new Map();
    combinedRows.forEach((row) => {
        map.set(getIndividualPerformanceKey(row), row.rank);
    });
    return map;
};

const getIndividualSourceRows = (classSize) => {
    const mergerEra = individualHistoryData.mergerEra;
    const combined = buildCombinedIndividualRows(mergerEra);
    const overallRankByKey = buildOverallRankByPerformanceKey(combined);
    if (classSize === 'all') {
        return combined.map((row) => ({ ...row, overallRank: row.rank }));
    }
    return (mergerEra[classSize] || []).map((row) => ({
        ...row,
        overallRank: overallRankByKey.get(getIndividualPerformanceKey(row)),
    }));
};

const filterIndividualRows = (rows, search, year) => {
    const scoped = year != null ? rows.filter((row) => row.year === year) : rows;
    const ranked = rankIndividualRows(scoped);
    const query = search.trim().toLowerCase();
    if (!query) {
        return ranked;
    }
    return ranked.filter(
        (row) =>
            row.player.toLowerCase().includes(query)
            || row.school.toLowerCase().includes(query)
    );
};

const RECORD_COLUMNS = {
    honors: [
        { key: 'championships', label: 'Championships' },
        { key: 'runnerUp', label: 'Runner-Up' },
        { key: 'finalsAppearances', label: 'Finals Appearances' },
    ],
    open: [
        { key: 'championships', label: 'Championships' },
        { key: 'runnerUp', label: 'Runner-Up' },
        { key: 'thirdPlace', label: 'Third Place' },
        { key: 'podiums', label: 'Podiums' },
    ],
    merger: [
        { key: 'championships', label: 'Championships' },
        { key: 'runnerUp', label: 'Runner-Up' },
        { key: 'thirdPlace', label: 'Third Place' },
        { key: 'podiums', label: 'Podiums' },
    ],
};

const StateChampionshipQualified = () => (
    <InfoCard title="Qualified Teams" icon={Trophy}>
        <p>
            These are the schools that are known to have qualified for the State Championship. If you believe your school should be listed here, please contact us at <a href="mailto:miqbaorg@gmail.com" className="text-blue-400 hover:underline">miqbaorg@gmail.com</a>
        </p>
        <div>
            <iframe
                width="100%"
                height="1200px"
                title="Michigan State Championship Qualified Teams"
                src="https://docs.google.com/spreadsheets/d/e/2PACX-1vQl2HxSCjQ_Yl9Zqt3y8KjM6EMXIunzoscq1D9dhheJLOrLqf5DQx2fCwVZHQMEo4GtKz3iz_f3614R/pubhtml?gid=1873823540&amp;single=true&amp;widget=true&amp;headers=false"></iframe>
        </div>
    </InfoCard>
);

const StateChampionshipHistory = () => {
    const [mode, setMode] = useState('era');
    const [era, setEra] = useState('merger');
    const [view, setView] = useState('yearly');
    const [classSize, setClassSize] = useState('classA');
    const [search, setSearch] = useState('');
    const [selectedSchool, setSelectedSchool] = useState('');
    const [individualYear, setIndividualYear] = useState(null);

    const handleHistoryModeChange = (nextMode) => {
        if (nextMode === 'individuals') {
            setClassSize('all');
        } else if (classSize === 'all') {
            setClassSize('classA');
        }
        setMode(nextMode);
        setSearch('');
        setSelectedSchool('');
        setIndividualYear(null);
    };

    const rawRows = mode === 'era' ? getHistoryRows(era, view, classSize) : [];
    const filteredRows = useMemo(() => {
        if (mode !== 'era') {
            return [];
        }
        if (view === 'yearly') {
            return filterYearlyRows(rawRows, search);
        }
        return filterRecordRows(rawRows, search);
    }, [mode, rawRows, search, view]);

    const individualRows = useMemo(
        () => filterIndividualRows(getIndividualSourceRows(classSize), search, individualYear),
        [classSize, search, individualYear]
    );

    const schoolPlacements = useMemo(() => (
        [...schoolHistoryIndex.placements].sort((a, b) => b.year - a.year)
    ), []);

    const showThirdPlace = view === 'yearly' && era !== 'honors';

    return (
        <div className="space-y-8">
            <InfoCard title="State Championship History" icon={Trophy}>
                <p className="text-gray-400">
                    Explore Michigan high school quiz bowl state championship results by era, look up a school&apos;s team and individual finishes, or browse Merger Era top individual performances.
                </p>
            </InfoCard>
            <HistoryFilterBar
                mode={mode}
                onModeChange={handleHistoryModeChange}
                era={era}
                setEra={setEra}
                view={view}
                setView={setView}
                classSize={classSize}
                setClassSize={setClassSize}
                search={search}
                setSearch={setSearch}
                selectedSchool={selectedSchool}
                setSelectedSchool={setSelectedSchool}
                schoolOptions={schoolHistoryIndex.schools}
                individualYear={individualYear}
                setIndividualYear={setIndividualYear}
            />
            <div className="bg-gray-800/60 rounded-lg border border-gray-700 p-4">
                {mode === 'era' && (
                    view === 'yearly' ? (
                        <HistoryYearlyTable rows={filteredRows} showThirdPlace={showThirdPlace} />
                    ) : (
                        <HistoryRecordsTable rows={filteredRows} columns={RECORD_COLUMNS[era]} />
                    )
                )}
                {mode === 'school' && (
                    <HistorySchoolTable
                        placements={schoolPlacements}
                        individuals={schoolHistoryIndex.individuals}
                        selectedSchool={selectedSchool}
                    />
                )}
                {mode === 'individuals' && (
                    <HistoryIndividualTable
                        rows={individualRows}
                        showClass={classSize === 'all'}
                        showOverallRank={classSize !== 'all' || individualYear != null}
                    />
                )}
            </div>
        </div>
    );
};

export default function App() {
    // Helper: Get the current page from the URL hash, or default to 'Home'
    const getPageFromHash = () => {
        const hash = window.location.hash.replace('#', '');
        // Handle URL encoding (e.g., "State%20Championship" -> "State Championship")
        const decodedHash = decodeURIComponent(hash);

        // Check if the hash matches a valid page, otherwise default to Home
        const validPages = [
            'Home', 'Bylaws', 'Board', 'MIQBAFormat', 'Schedule', 'Teams',
            'CoachResources', 'PlayerResources', 'StudyResources', 'TournamentDirectorResources', 'ScheduleGenerator',
            'State Championship', 'StateChampionshipQualified', 'StateChampionshipHistory', 'Contact',
        ];
        if (decodedHash === 'Teams') {
            return 'CoachResources';
        }
        if (decodedHash === 'State Championship') {
            return 'StateChampionshipQualified';
        }
        return validPages.includes(decodedHash) ? decodedHash : 'Home';
    };

    const [currentPage, setCurrentPage] = useState(getPageFromHash());

    // Effect: Listen for browser Back/Forward button clicks
    useEffect(() => {
        const handleHashChange = () => {
            setCurrentPage(getPageFromHash());
        };

        window.addEventListener('hashchange', handleHashChange);
        return () => window.removeEventListener('hashchange', handleHashChange);
    }, []);

    // Wrapper: When a user clicks a link, update the URL Hash.
    // The useEffect above will detect this change and update the visual state.
    const navigateToPage = (pageName) => {
        window.location.hash = pageName;
    };

    const pages = [
        { display: 'Home', name: 'Home', icon: HomeIcon, component: <Home setCurrentPage={navigateToPage} /> },
        { display: 'Bylaws', name: 'Bylaws', icon: FileText, component: <Bylaws /> },
        { display: 'Board', name: 'Board', icon: Info, component: <Board /> },
        { display: 'MIQBA Format', name: 'MIQBAFormat', icon: BriefcaseBusiness, component: <MIQBAFormat /> },
        { display: 'Schedule', name: 'Schedule', icon: Calendar, component: <Schedule /> },
        { display: 'Coach Resources', name: 'CoachResources', icon: Users, component: <CoachResources /> },
        { display: 'Player Resources', name: 'PlayerResources', icon: User, component: <PlayerResources setCurrentPage={navigateToPage} /> },
        { display: 'Study Resources', name: 'StudyResources', icon: BookOpen, component: <StudyResources setCurrentPage={navigateToPage} /> },
        { display: 'Run a Tournament', name: 'TournamentDirectorResources', icon: ClipboardList, component: <TournamentDirectorResources setCurrentPage={navigateToPage} /> },
        { display: 'MIQBA Schedule Generator', name: 'ScheduleGenerator', icon: CalendarRange, component: <ScheduleGeneratorPage setCurrentPage={navigateToPage} /> },
        { display: 'Qualified Teams', name: 'StateChampionshipQualified', icon: Trophy, component: <StateChampionshipQualified /> },
        { display: 'History', name: 'StateChampionshipHistory', icon: BookOpen, component: <StateChampionshipHistory /> },
        { display: 'Contact', name: 'Contact', icon: Mail, component: <Contact /> },
    ];

    const aboutUsNav = [
        { display: 'Bylaws', name: 'Bylaws', icon: FileText },
        { display: 'MIQBA Format', name: 'MIQBAFormat', icon: BriefcaseBusiness },
        { display: 'Board', name: 'Board', icon: Info },
    ];

    const resourcesNav = [
        { display: 'Coach Resources', name: 'CoachResources', icon: Users },
        { display: 'Player Resources', name: 'PlayerResources', icon: User },
        { display: 'Study Resources', name: 'StudyResources', icon: BookOpen },
        { display: 'Run a Tournament', name: 'TournamentDirectorResources', icon: ClipboardList },
    ];

    const stateChampionshipNav = [
        { display: 'Qualified Teams', name: 'StateChampionshipQualified', icon: Trophy },
        { display: 'History', name: 'StateChampionshipHistory', icon: BookOpen },
    ];

    const navItems = [
        { display: 'Home', name: 'Home', icon: HomeIcon },
        { display: 'About Us', name: 'AboutUs', icon: Info, children: aboutUsNav },
        { display: 'Schedule', name: 'Schedule', icon: Calendar },
        { display: 'Resources', name: 'Resources', icon: Users, children: resourcesNav },
        { display: 'State Championship', name: 'StateChampionship', icon: Trophy, children: stateChampionshipNav },
        { display: 'Contact', name: 'Contact', icon: Mail },
    ];

    const ActivePageComponent = pages.find(p => p.name === currentPage)?.component || <Home setCurrentPage={navigateToPage} />;

    return (
        <div className="min-h-screen bg-gray-900 text-gray-300 font-sans">
            <Navbar
                currentPage={currentPage}
                setCurrentPage={navigateToPage}
                navItems={navItems}
            />

            <main className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
                {ActivePageComponent}
            </main>

            <Footer />
        </div>
    );
}